/**
 * ConsentCheckinModal
 *
 * Modal bloqueante que aparece para un miembro inscrito a un evento mientras
 * no haya completado el "Check-in de documentos" del evento.
 *
 * Pasos (stepper interno):
 *  1. Documento de identidad (upload)
 *  2. Certificado EPS (upload)
 *  3. Tratamiento de datos (aceptar)
 *  4. Autorización de participación:
 *      - Si menor: capturar datos de uno o dos padres + firma + foto verificación
 *      - Si adulto: firma propia
 *     Al final genera el PDF, lo sube y marca completed.
 *
 * Props:
 *  - event, registration, member, person + datos de contexto (clubName, churchName, etc.)
 *  - onCompleted callback opcional cuando termina exitoso.
 *  - canDismiss: si true, el usuario puede cerrar (ej: admin viendo el modal en preview).
 *    Por defecto false → bloqueante hasta completar.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EncryptionMeta } from "@/lib/services/crypto";
import { useDecryptedUrl } from "@/hooks/useDecryptedUrl";
import {
  CheckCircle2,
  AlertCircle,
  Upload,
  Camera,
  RefreshCw,
  FileText,
  Loader2,
  Eye,
  Pencil,
} from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Select from "@/components/ui/Select";
import { EncryptedDocumentView } from "@/components/ui/EncryptedDocumentView";
import SignaturePad from "./SignaturePad";
import ParentPhotoCapture from "./ParentPhotoCapture";
import PdfPreview from "./PdfPreview";
import DocumentScanner from "@/pages/profile/components/DocumentScanner";
import {
  buildChecklist,
  initConsent,
  setConsentIdDocumentFront,
  setConsentIdDocumentBack,
  setConsentEpsCertificate,
  setConsentMedicalAndTravel,
  acceptDataConsent,
  setParticipantSignature,
  setParentSignatures,
  setConsentParticipantPhoto,
  completeConsent,
  getConsentById,
  isConsentCompleted,
} from "@/lib/services/eventConsents";
import {
  uploadConsentIdDocumentFront,
  uploadConsentIdDocumentBack,
  uploadConsentEpsCertificate,
  uploadConsentSignature,
  uploadConsentParentPhoto,
  uploadConsentParticipantPhoto,
  uploadConsentPdf,
  uploadMemberPhoto,
  dataUrlToBlob,
} from "@/lib/services/storage";
import {
  generateMinorConsentPdf,
  generateAdultConsentPdf,
} from "@/lib/pdf/consentPdf";
import { personService } from "@/lib/services/persons";
import { useAuthStore } from "@/stores/authStore";
import EpsSelect from "@/components/forms/EpsSelect";
import { Timestamp } from "firebase/firestore";
import type {
  Event,
  EventRegistration,
  Member,
  Person,
  EventConsent,
  ConsentDocument,
  ConsentParentRecord,
  IdDocumentType,
} from "@/types";

export interface ConsentCheckinModalProps {
  isOpen: boolean;
  /** Si canDismiss=false, este onClose solo se llama tras completar */
  onClose: () => void;
  event: Event;
  registration: EventRegistration;
  member: Member;
  person: Person;
  clubName: string;
  associationName: string;
  churchName?: string;
  districtName?: string;
  boardVoteNumber?: string;
  boardVoteDate?: string;
  /** Si true, permite cerrar sin completar (para preview/admin) */
  canDismiss?: boolean;
  onCompleted?: (consent: EventConsent) => void;
}

type Step = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/** Sub-paso interno del paso 6 (Autorización de participación). */
type Step6Substep = "form" | "preview" | "sign";

interface ParentDraft {
  fullName: string;
  idDocumentType: IdDocumentType;
  idDocumentNumber: string;
  address: string;
  relationship: "padre" | "madre" | "acudiente";
  signatureDataUrl?: string;
  signatureUploaded?: { url: string; path: string };
  photoFile?: File;
  photoUploaded?: {
    url: string;
    path: string;
    contentType: string;
    encryption: { key: string; alg: string };
  };
}

const emptyParent = (): ParentDraft => ({
  fullName: "",
  idDocumentType: "CC",
  idDocumentNumber: "",
  address: "",
  relationship: "padre",
});

export function ConsentCheckinModal({
  isOpen,
  onClose,
  event,
  registration,
  member,
  person,
  clubName,
  associationName,
  churchName,
  districtName,
  boardVoteNumber,
  boardVoteDate,
  canDismiss = false,
  onCompleted,
}: ConsentCheckinModalProps) {
  const { user } = useAuthStore();
  const [consent, setConsent] = useState<EventConsent | null>(null);
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ----- scanner de documentos (pasos 1, 2 y 3) -----
  const fileInputRefFront = useRef<HTMLInputElement>(null);
  const fileInputRefBack = useRef<HTMLInputElement>(null);
  const fileInputRefEps = useRef<HTMLInputElement>(null);
  const [scanningStep, setScanningStep] = useState<1 | 2 | 3 | null>(null);
  const [previewUrlFront, setPreviewUrlFront] = useState<string | null>(null);
  const [previewUrlBack, setPreviewUrlBack] = useState<string | null>(null);
  const [previewUrlEps, setPreviewUrlEps] = useState<string | null>(null);

  // ----- estado de paso 4 (datos médicos y viaje) -----
  const [medicalDraft, setMedicalDraft] = useState({
    healthInsurance: "",
    noMedicalConditions: false,
    medicalConditions: "",
    allergies: "",
    medications: "",
    departureCity: "",
    returnCity: "",
    idExpeditionCity: "",
  });

  // ----- estado de paso 5 (firma tratamiento de datos) -----
  const [dataConsentSignature, setDataConsentSignature] = useState<
    string | null
  >(null);

  // ----- estado de paso 6 (firmas autorización + vista previa PDF) -----
  const [participantSignature, setParticipantSig] = useState<string | null>(
    null,
  );
  const [parents, setParents] = useState<ParentDraft[]>([emptyParent()]);
  const [step6Substep, setStep6Substep] = useState<Step6Substep>("form");
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);

  // ----- estado de paso 7 (foto participante) -----
  const [participantPhotoFile, setParticipantPhotoFile] = useState<File | null>(
    null,
  );
  const [participantPhotoPreview, setParticipantPhotoPreview] = useState<
    string | null
  >(null);

  // Bug fix 2026-04-26: ParentPhotoCapture revoca su blob URL en el unmount.
  // Cuando capturamos en paso 7 y el JSX cambia a <img>, el componente se
  // desmonta y la URL queda inválida (ERR_FILE_NOT_FOUND). Mantenemos
  // nuestra propia URL creada desde el File y la revocamos al cambiar.
  const ownedParticipantPhotoUrlRef = useRef<string | null>(null);
  const setParticipantPhoto = (file: File | null) => {
    setParticipantPhotoFile(file);
    if (ownedParticipantPhotoUrlRef.current) {
      URL.revokeObjectURL(ownedParticipantPhotoUrlRef.current);
      ownedParticipantPhotoUrlRef.current = null;
    }
    if (file) {
      const url = URL.createObjectURL(file);
      ownedParticipantPhotoUrlRef.current = url;
      setParticipantPhotoPreview(url);
    } else {
      setParticipantPhotoPreview(null);
    }
  };
  useEffect(() => {
    return () => {
      if (ownedParticipantPhotoUrlRef.current) {
        URL.revokeObjectURL(ownedParticipantPhotoUrlRef.current);
        ownedParticipantPhotoUrlRef.current = null;
      }
    };
  }, []);

  // Inicializar el consentimiento al abrir
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const c = await initConsent({
          event,
          registration,
          member,
          person,
          clubName,
          associationName,
          churchName,
          districtName,
          boardVoteNumber,
          boardVoteDate,
          userId: user?.uid,
        });
        if (cancelled) return;
        // Autofill: si la persona ya tiene documentos en su perfil
        // (cargados por ella misma, el director u otro evento previo),
        // los reusamos en el consent y saltamos el paso. No preguntamos.
        let cFilled = c;
        try {
          const tasks: Promise<void>[] = [];
          if (
            !c.idDocumentFront &&
            person.documentFrontUrl &&
            person.documentFrontPath
          ) {
            tasks.push(
              setConsentIdDocumentFront(
                event.id,
                member.id,
                {
                  url: person.documentFrontUrl,
                  path: person.documentFrontPath,
                  contentType: person.documentFrontContentType || "image/jpeg",
                  encryption: person.documentFrontEncryption,
                  uploadedAt: Timestamp.now(),
                },
                user?.uid,
              ),
            );
          }
          if (
            !c.idDocumentBack &&
            person.documentBackUrl &&
            person.documentBackPath
          ) {
            tasks.push(
              setConsentIdDocumentBack(
                event.id,
                member.id,
                {
                  url: person.documentBackUrl,
                  path: person.documentBackPath,
                  contentType: person.documentBackContentType || "image/jpeg",
                  encryption: person.documentBackEncryption,
                  uploadedAt: Timestamp.now(),
                },
                user?.uid,
              ),
            );
          }
          if (
            !c.epsCertificate &&
            person.epsDocumentUrl &&
            person.epsDocumentPath
          ) {
            tasks.push(
              setConsentEpsCertificate(
                event.id,
                member.id,
                {
                  url: person.epsDocumentUrl,
                  path: person.epsDocumentPath,
                  contentType: person.epsDocumentContentType || "image/jpeg",
                  encryption: person.epsDocumentEncryption,
                  uploadedAt: Timestamp.now(),
                },
                user?.uid,
              ),
            );
          }
          if (!c.participantPhoto && person.photoUrl) {
            // photoPath puede no existir si la foto vino del proveedor de Auth
            // (Google/Microsoft) — usamos la URL directa igual.
            tasks.push(
              setConsentParticipantPhoto(
                event.id,
                member.id,
                {
                  url: person.photoUrl,
                  path: person.photoPath || "",
                  contentType: "image/jpeg",
                  uploadedAt: Timestamp.now(),
                },
                user?.uid,
              ),
            );
          }
          if (tasks.length > 0) {
            await Promise.all(tasks);
            const refreshed = await getConsentById(event.id, member.id);
            if (refreshed) cFilled = refreshed;
          }
        } catch (autofillErr) {
          // No bloquear el check-in si el autofill falla; el usuario podrá cargar manualmente.
          // eslint-disable-next-line no-console
          console.warn(
            "[ConsentCheckin] autofill desde Person falló",
            autofillErr,
          );
        }
        if (cancelled) return;
        setConsent(cFilled);
        // Avanzar al primer paso pendiente
        const checklist = buildChecklist(cFilled, event.consentChecklistConfig);
        // Solo los pasos HABILITADOS por el evento cuentan como pendientes:
        // los deshabilitados no aparecen ni se piden.
        const firstPendingIdx = checklist.findIndex(
          (i) => i.required && !i.done,
        );
        if (firstPendingIdx >= 0) {
          setStep((firstPendingIdx + 1) as Step);
        } else {
          setStep(7);
        }
      } catch (e) {
        console.error("[ConsentCheckin] init error", e);
        setError("No se pudo inicializar el consentimiento");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    event,
    registration,
    member,
    person,
    clubName,
    associationName,
    churchName,
    districtName,
    boardVoteNumber,
    boardVoteDate,
    user?.uid,
  ]);

  const checklist = useMemo(
    () => buildChecklist(consent, event.consentChecklistConfig),
    [consent, event.consentChecklistConfig],
  );

  // La config del evento MANDA: los pasos que el organizador deshabilitó no
  // aparecen en el asistente. Antes solo quedaban "opcionales" y el director
  // veía pedirse datos que nunca habilitó (campamento 2026-08-10: pedía
  // "Datos médicos y del viaje" con medicalAndTravel: false). El resto del
  // sistema ya cuenta solo los required (progressFor, isConsentCompleted);
  // esto alinea el asistente con esa misma regla. Los números internos de
  // paso (1..7) no cambian — solo se ocultan y se saltan los deshabilitados.
  const visibles = useMemo(
    () => checklist.filter((i) => i.required),
    [checklist],
  );
  /** Posición 1-based del paso actual entre los VISIBLES (0 si está oculto —
   *  solo pasa al entrar por un salto explícito, p. ej. "editar datos" desde
   *  la vista previa del PDF). */
  const posicionVisible = useMemo(() => {
    const id = checklist[step - 1]?.id;
    return visibles.findIndex((i) => i.id === id) + 1;
  }, [checklist, visibles, step]);
  /** Siguiente paso VISIBLE después de `desde`; si no queda ninguno, se queda
   *  (el cierre lo hace el guardado de la foto, hoy siempre habilitada). */
  const siguienteVisible = useCallback(
    (desde: Step): Step => {
      for (let k = desde; k < checklist.length; k++) {
        if (checklist[k].required) return (k + 1) as Step;
      }
      return desde;
    },
    [checklist],
  );

  const isMinor = consent?.participantType === "minor";

  // Prefill del paso 4 con datos ya conocidos (Person + snapshot previo si retoma)
  useEffect(() => {
    if (!consent) return;
    const s = consent.formSnapshot;
    setMedicalDraft({
      healthInsurance: s.memberHealthInsurance ?? person.healthInsurance ?? "",
      noMedicalConditions:
        s.memberNoMedicalConditions ?? person.noMedicalConditions ?? false,
      medicalConditions:
        s.memberMedicalConditions ?? person.medicalConditions ?? "",
      allergies: s.memberAllergies ?? person.allergies ?? "",
      medications: s.memberMedications ?? person.medications ?? "",
      departureCity: s.departureCity ?? "",
      returnCity: s.returnCity ?? "",
      idExpeditionCity:
        s.memberIdExpeditionCity ?? person.idExpeditionCity ?? "",
    });
  }, [consent, person]);

  const reload = async () => {
    const c = await getConsentById(event.id, member.id);
    setConsent(c);
    return c;
  };

  // Bug fix 2026-04-26: si el usuario llega al paso 6 como ADULTO con
  // substep "form" (caso al reabrir el modal o navegar por las píldoras del
  // stepper), no había nada que renderizar. Forzamos substep "preview" y
  // generamos el PDF si todavía no existe.
  // Bug fix 2026-04-26 (b): tras editar datos médicos desde el preview, al
  // volver al paso 6 el `previewPdfUrl` quedaba en null (lo limpiamos al
  // navegar a paso 4) y el modal mostraba el spinner para siempre. Aquí
  // regeneramos el preview si está vacío, tanto para adulto como menor.
  const regenInFlightRef = useRef(false);
  useEffect(() => {
    if (!consent) return;
    if (step !== 6) return;
    const needsRegen = !previewPdfUrl && !regenInFlightRef.current;
    if (consent.participantType === "adult") {
      if (step6Substep === "form") setStep6Substep("preview");
      if (needsRegen) {
        regenInFlightRef.current = true;
        void regenerateAdultPreview(consent)
          .catch((e) => {
            console.error("[ConsentCheckin] regen adult preview", e);
            setError("No se pudo generar la vista previa");
          })
          .finally(() => {
            regenInFlightRef.current = false;
          });
      }
    } else if (consent.participantType === "minor") {
      if (step6Substep === "preview" && needsRegen) {
        regenInFlightRef.current = true;
        void regenerateMinorPreview(consent)
          .catch((e) => {
            console.error("[ConsentCheckin] regen minor preview", e);
            setError("No se pudo generar la vista previa");
          })
          .finally(() => {
            regenInFlightRef.current = false;
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, step6Substep, consent?.participantType, previewPdfUrl]);

  // Scroll-to-error: cuando aparece un error de validación, llevamos el
  // scroll del modal al banner para que el usuario sepa qué corregir.
  const errorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!error) return;
    requestAnimationFrame(() => {
      errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [error]);

  // ============ Step 1: ID document FRONT ============
  const handleIdFrontUpload = async (file: File) => {
    if (!consent) return;
    setLoading(true);
    setError(null);
    try {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrlFront(objectUrl);
      const r = await uploadConsentIdDocumentFront(
        file,
        event.id,
        member.id,
        person?.id,
      );
      await setConsentIdDocumentFront(
        event.id,
        member.id,
        {
          url: r.url,
          path: r.path,
          contentType: file.type,
          encryption: r.encryption,
          uploadedAt: Timestamp.now(),
          fileName: file.name,
        },
        user?.uid,
      );
      // Sincronizar con Person (perfil) usando el mirror en `persons/{personId}/...`
      if (person?.id) {
        const mirror = r.mirrors?.[0];
        await personService.updateDocumentFront(person.id, {
          url: mirror?.url ?? r.url,
          path: mirror?.path ?? r.path,
          contentType: file.type,
          encryption: r.encryption,
        });
      }
      await reload();
      setStep(siguienteVisible(1));
    } catch (e) {
      console.error(e);
      setError("Error subiendo el anverso del documento");
    } finally {
      setLoading(false);
    }
  };

  // ============ Step 2: ID document BACK ============
  const handleIdBackUpload = async (file: File) => {
    if (!consent) return;
    setLoading(true);
    setError(null);
    try {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrlBack(objectUrl);
      const r = await uploadConsentIdDocumentBack(
        file,
        event.id,
        member.id,
        person?.id,
      );
      await setConsentIdDocumentBack(
        event.id,
        member.id,
        {
          url: r.url,
          path: r.path,
          contentType: file.type,
          encryption: r.encryption,
          uploadedAt: Timestamp.now(),
          fileName: file.name,
        },
        user?.uid,
      );
      // Sincronizar con Person (perfil) usando el mirror
      if (person?.id) {
        const mirror = r.mirrors?.[0];
        await personService.updateDocumentBack(person.id, {
          url: mirror?.url ?? r.url,
          path: mirror?.path ?? r.path,
          contentType: file.type,
          encryption: r.encryption,
        });
      }
      await reload();
      setStep(siguienteVisible(2));
    } catch (e) {
      console.error(e);
      setError("Error subiendo el reverso del documento");
    } finally {
      setLoading(false);
    }
  };

  // ============ Step 3: EPS ============
  const handleEpsUpload = async (file: File) => {
    if (!consent) return;
    setLoading(true);
    setError(null);
    try {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrlEps(objectUrl);
      const r = await uploadConsentEpsCertificate(
        file,
        event.id,
        member.id,
        person?.id,
      );
      await setConsentEpsCertificate(
        event.id,
        member.id,
        {
          url: r.url,
          path: r.path,
          contentType: file.type,
          encryption: r.encryption,
          uploadedAt: Timestamp.now(),
          fileName: file.name,
        },
        user?.uid,
      );
      // Sincronizar con Person (perfil) usando el mirror
      if (person?.id) {
        const mirror = r.mirrors?.[0];
        await personService.updateEpsDocument(person.id, {
          url: mirror?.url ?? r.url,
          path: mirror?.path ?? r.path,
          contentType: file.type,
          encryption: r.encryption,
        });
      }
      await reload();
      setStep(siguienteVisible(3));
    } catch (e) {
      console.error(e);
      setError("Error subiendo certificado de EPS");
    } finally {
      setLoading(false);
    }
  };

  // ============ Step 4: Datos médicos y de viaje ============
  const handleSubmitMedicalAndTravel = async () => {
    if (!consent) return;
    // Validaciones mínimas
    if (!medicalDraft.healthInsurance.trim()) {
      setError("Debe indicar la EPS o entidad de salud");
      return;
    }
    if (!medicalDraft.departureCity.trim()) {
      setError("Debe indicar la ciudad de salida");
      return;
    }
    if (!medicalDraft.returnCity.trim()) {
      setError("Debe indicar la ciudad de regreso");
      return;
    }
    if (!isMinor && !medicalDraft.idExpeditionCity.trim()) {
      setError("Debe indicar el lugar de expedición del documento");
      return;
    }
    if (
      !medicalDraft.noMedicalConditions &&
      !medicalDraft.medicalConditions.trim() &&
      !medicalDraft.allergies.trim() &&
      !medicalDraft.medications.trim()
    ) {
      setError(
        'Marca "No presento condiciones m\u00e9dicas relevantes" o describe condiciones, alergias o medicamentos',
      );
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await setConsentMedicalAndTravel(
        event.id,
        member.id,
        {
          healthInsurance: medicalDraft.healthInsurance.trim(),
          medicalConditions: medicalDraft.noMedicalConditions
            ? ""
            : medicalDraft.medicalConditions.trim(),
          allergies: medicalDraft.noMedicalConditions
            ? ""
            : medicalDraft.allergies.trim(),
          medications: medicalDraft.noMedicalConditions
            ? ""
            : medicalDraft.medications.trim(),
          noMedicalConditions: medicalDraft.noMedicalConditions,
          idExpeditionCity: medicalDraft.idExpeditionCity.trim() || undefined,
          departureCity: medicalDraft.departureCity.trim(),
          returnCity: medicalDraft.returnCity.trim(),
        },
        user?.uid,
      );
      // Sincronizar a Person para no volver a preguntar en futuros eventos
      if (person?.id) {
        try {
          await personService.updateMedicalInfo(person.id, {
            healthInsurance: medicalDraft.healthInsurance.trim(),
            medicalConditions: medicalDraft.noMedicalConditions
              ? ""
              : medicalDraft.medicalConditions.trim(),
            allergies: medicalDraft.noMedicalConditions
              ? ""
              : medicalDraft.allergies.trim(),
            medications: medicalDraft.noMedicalConditions
              ? ""
              : medicalDraft.medications.trim(),
            noMedicalConditions: medicalDraft.noMedicalConditions,
            idExpeditionCity: medicalDraft.idExpeditionCity.trim() || undefined,
          });
        } catch (errSync) {
          // No bloquear el flujo
          console.warn(
            "[ConsentCheckin] No se pudo sincronizar datos m\u00e9dicos al perfil",
            errSync,
          );
        }
      }
      await reload();
      setStep(siguienteVisible(4));
    } catch (e) {
      console.error(e);
      setError("Error guardando datos m\u00e9dicos y de viaje");
    } finally {
      setLoading(false);
    }
  };

  // ============ Step 5: data consent CON FIRMA ============
  const handleAcceptData = async () => {
    if (!consent) return;
    if (!dataConsentSignature) {
      setError("Debe firmar para aceptar el tratamiento de datos");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Subir la firma
      const sigBlob = dataUrlToBlob(dataConsentSignature);
      const sigUp = await uploadConsentSignature(
        sigBlob,
        event.id,
        member.id,
        "data-consent",
      );
      await acceptDataConsent(
        event.id,
        member.id,
        { url: sigUp.url, path: sigUp.path, signedAt: Timestamp.now() },
        navigator.userAgent,
        user?.uid,
      );
      const updated = await reload();
      // Para adulto entramos directo a "preview"; para menor a "form".
      setStep6Substep(
        updated?.participantType === "minor" ? "form" : "preview",
      );
      // Si adulto: generar el preview PDF inmediatamente
      if (updated?.participantType === "adult") {
        await regenerateAdultPreview(updated);
      }
      setStep(siguienteVisible(5));
    } catch (e) {
      console.error(e);
      setError("Error aceptando tratamiento de datos");
    } finally {
      setLoading(false);
    }
  };

  // ============ Step 6: signatures (participación) ============

  // Limpia preview anterior si lo hay
  const clearPreviewPdf = () => {
    if (previewPdfUrl) URL.revokeObjectURL(previewPdfUrl);
    setPreviewPdfUrl(null);
  };

  // Genera PDF preview para adulto SIN firma incrustada
  const regenerateAdultPreview = async (c: EventConsent) => {
    clearPreviewPdf();
    const blob = await generateAdultConsentPdf({
      snapshot: c.formSnapshot,
    });
    const url = URL.createObjectURL(blob);
    setPreviewPdfUrl(url);
  };

  // Genera PDF preview para menor SIN firmas (sí muestra datos de padres ya capturados)
  const regenerateMinorPreview = async (c: EventConsent) => {
    clearPreviewPdf();
    const previewParents: ConsentParentRecord[] = parents.map((p) => ({
      fullName: p.fullName,
      idDocumentType: p.idDocumentType,
      idDocumentNumber: p.idDocumentNumber,
      address: p.address,
      relationship: p.relationship,
      // Sin firma ni foto reales en el preview (placeholders vacíos para satisfacer el tipo;
      // el generador del PDF omite la imagen si la URL está vacía).
      signature: {
        url: "",
        path: "",
        signedAt: Timestamp.now(),
      },
      verificationPhoto: {
        url: "",
        path: "",
        contentType: "",
        encryption: { key: "", alg: "" },
        uploadedAt: Timestamp.now(),
      },
    }));
    const blob = await generateMinorConsentPdf({
      snapshot: c.formSnapshot,
      parents: previewParents,
    });
    const url = URL.createObjectURL(blob);
    setPreviewPdfUrl(url);
  };

  // Validar datos padres del menor antes del preview
  const validateMinorForm = (): string | null => {
    for (const [idx, p] of parents.entries()) {
      if (!p.fullName || !p.idDocumentNumber || !p.address || !p.photoFile) {
        return `Complete los datos y la foto del padre/acudiente ${idx + 1}`;
      }
    }
    return null;
  };

  const handleMinorGoToPreview = async () => {
    if (!consent) return;
    const err = validateMinorForm();
    if (err) {
      setError(err);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await regenerateMinorPreview(consent);
      setStep6Substep("preview");
    } catch (e) {
      console.error(e);
      setError("No se pudo generar la vista previa");
    } finally {
      setLoading(false);
    }
  };

  const handleAdultSubmit = async () => {
    if (!consent || !participantSignature) {
      setError("Debe firmar antes de continuar");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Subir firma
      const sigBlob = dataUrlToBlob(participantSignature);
      const sigUp = await uploadConsentSignature(
        sigBlob,
        event.id,
        member.id,
        "member",
      );
      await setParticipantSignature(
        event.id,
        member.id,
        { url: sigUp.url, path: sigUp.path, signedAt: Timestamp.now() },
        user?.uid,
      );

      // Generar PDF
      const pdfBlob = await generateAdultConsentPdf({
        snapshot: consent.formSnapshot,
        participantSignatureDataUrl: participantSignature,
      });
      const pdfUp = await uploadConsentPdf(pdfBlob, event.id, member.id);
      await completeConsent(
        event.id,
        member.id,
        {
          url: pdfUp.url,
          path: pdfUp.path,
          contentType: "application/pdf",
          encryption: pdfUp.encryption,
          uploadedAt: Timestamp.now(),
          fileName: "consentimiento.pdf",
        },
        user?.uid,
      );
      await reload();
      clearPreviewPdf();
      setStep(7); // paso 7: foto del participante
    } catch (e) {
      console.error(e);
      setError("Error generando el consentimiento. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  const handleMinorSubmit = async () => {
    if (!consent) return;
    // Validaciones
    for (const [idx, p] of parents.entries()) {
      if (
        !p.fullName ||
        !p.idDocumentNumber ||
        !p.address ||
        !p.signatureDataUrl ||
        !p.photoFile
      ) {
        setError(
          `Complete todos los campos, firma y foto del padre/acudiente ${idx + 1}`,
        );
        return;
      }
    }
    setLoading(true);
    setError(null);
    try {
      // Subir firmas y fotos de cada padre
      const records: ConsentParentRecord[] = [];
      for (let i = 0; i < parents.length; i++) {
        const p = parents[i];
        const sigBlob = dataUrlToBlob(p.signatureDataUrl as string);
        const sigUp = await uploadConsentSignature(
          sigBlob,
          event.id,
          member.id,
          `parent-${i + 1}`,
        );
        const photoUp = await uploadConsentParentPhoto(
          p.photoFile as File,
          event.id,
          member.id,
          `parent-${i + 1}`,
        );
        records.push({
          fullName: p.fullName,
          idDocumentType: p.idDocumentType,
          idDocumentNumber: p.idDocumentNumber,
          address: p.address,
          relationship: p.relationship,
          signature: {
            url: sigUp.url,
            path: sigUp.path,
            signedAt: Timestamp.now(),
          },
          verificationPhoto: {
            url: photoUp.url,
            path: photoUp.path,
            contentType: (p.photoFile as File).type,
            encryption: photoUp.encryption,
            uploadedAt: Timestamp.now(),
          },
        });
      }
      await setParentSignatures(event.id, member.id, records, user?.uid);

      // PDF
      const pdfBlob = await generateMinorConsentPdf({
        snapshot: consent.formSnapshot,
        parents: records,
      });
      const pdfUp = await uploadConsentPdf(pdfBlob, event.id, member.id);
      await completeConsent(
        event.id,
        member.id,
        {
          url: pdfUp.url,
          path: pdfUp.path,
          contentType: "application/pdf",
          encryption: pdfUp.encryption,
          uploadedAt: Timestamp.now(),
          fileName: "consentimiento.pdf",
        },
        user?.uid,
      );
      await reload();
      clearPreviewPdf();
      setStep(7); // paso 7: foto del participante
    } catch (e) {
      console.error(e);
      setError("Error generando el consentimiento. Intente nuevamente.");
    } finally {
      setLoading(false);
    }
  };

  // ============ Step 7: foto participante ============
  const handleParticipantPhotoSubmit = async () => {
    if (!consent || !participantPhotoFile) {
      setError("Debe capturar una foto del participante");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await uploadConsentParticipantPhoto(
        participantPhotoFile,
        event.id,
        member.id,
      );
      await setConsentParticipantPhoto(
        event.id,
        member.id,
        {
          url: r.url,
          path: r.path,
          contentType: participantPhotoFile.type,
          encryption: r.encryption,
          uploadedAt: Timestamp.now(),
          fileName: participantPhotoFile.name,
        },
        user?.uid,
      );
      // Sincronizar con Person (perfil) - solo si no tiene foto.
      // Subimos una copia NO encriptada a persons/{id}/photo para poder mostrarla
      // como avatar; la versión encriptada del consentimiento queda en su lugar.
      if (person?.id && !person.photoUrl) {
        try {
          const profileUp = await uploadMemberPhoto(
            participantPhotoFile,
            person.id,
          );
          await personService.updatePhoto(person.id, {
            url: profileUp.url,
            path: profileUp.path,
          });
        } catch (errPhoto) {
          // No bloquear el check-in por fallo en la foto de perfil
          // eslint-disable-next-line no-console
          console.warn(
            "[ConsentCheckin] No se pudo sincronizar foto al perfil",
            errPhoto,
          );
        }
      }
      const updated = await reload();
      if (updated) {
        // Notificar globalmente para que banners/badges de consentimientos pendientes
        // se refresquen sin necesidad de recargar la página.
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("consent:updated", {
              detail: { eventId: event.id, memberId: member.id },
            }),
          );
        }
        if (onCompleted) onCompleted(updated);
      }
    } catch (e) {
      console.error(e);
      setError("Error subiendo la foto del participante");
    } finally {
      setLoading(false);
    }
  };

  // ----- helpers parents UI -----
  const updateParent = (idx: number, patch: Partial<ParentDraft>) => {
    setParents((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    );
  };
  const addParent = () => {
    if (parents.length < 2) setParents([...parents, emptyParent()]);
  };
  const removeParent = (idx: number) => {
    setParents((prev) => prev.filter((_, i) => i !== idx));
  };

  // ============ RENDER ============
  if (!consent) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={canDismiss ? onClose : () => undefined}
        title="Check-in de documentos"
        size="lg"
        showCloseButton={canDismiss}
        closeOnOverlay={false}
        closeOnEscape={canDismiss}
      >
        <div className="p-6 text-center text-gray-600">
          {loading ? "Cargando consentimiento..." : (error ?? "")}
        </div>
      </Modal>
    );
  }

  const completed = isConsentCompleted(consent, event.consentChecklistConfig);

  // Etiquetas cortas para el stepper móvil
  const stepShortLabel = checklist[step - 1]?.label ?? "";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Check-in: ${event.name}`}
      description={`${person.fullName} · ${isMinor ? "Menor de edad" : "Mayor de edad"}`}
      size="full"
      showCloseButton
      closeOnOverlay={false}
      closeOnEscape={completed || canDismiss}
      className="!max-w-full !max-h-full !rounded-none sm:!max-w-3xl sm:!max-h-[92vh] sm:!rounded-2xl flex flex-col"
      headerClassName="p-4 sm:p-6 pb-2 sm:pb-0 flex-shrink-0"
      bodyClassName="!p-0 !max-h-none flex-1 min-h-0"
    >
      {/* Overlay global de loading mientras se sube/procesa algo */}
      {loading && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-[100] bg-white/85 backdrop-blur-sm flex flex-col items-center justify-center gap-3 pointer-events-auto"
        >
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin" />
          <p className="text-base font-semibold text-gray-800">Procesando…</p>
          <p className="text-xs text-gray-500 text-center px-6 max-w-xs">
            No cierres esta ventana. Estamos guardando tu información de forma
            segura.
          </p>
        </div>
      )}
      <div className="p-4 sm:p-6 pb-[max(1rem,env(safe-area-inset-bottom))]">
        {/* ===== Stepper móvil: barra de progreso + título de paso ===== */}
        <div className="mb-5 sticky top-0 -mx-4 sm:-mx-6 -mt-4 sm:-mt-6 px-4 sm:px-6 pt-3 pb-3 bg-white/95 backdrop-blur-sm z-10 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-primary-600 uppercase tracking-wide">
              {posicionVisible > 0
                ? `Paso ${posicionVisible} de ${visibles.length}`
                : checklist[step - 1]?.label}
            </span>
            <span className="text-xs text-gray-500">
              {visibles.filter((c) => c.done).length}/{visibles.length}{" "}
              completos
            </span>
          </div>
          <div className="flex gap-1.5 mb-2">
            {visibles.map((item, idx) => {
              const realIdx = checklist.findIndex((c) => c.id === item.id);
              const isCurrent = step === realIdx + 1;
              const isDone = item.done;
              return (
                <button
                  key={item.id}
                  type="button"
                  disabled={loading}
                  onClick={() => !loading && setStep((realIdx + 1) as Step)}
                  className="flex-1 group py-2 -my-2 touch-manipulation"
                  aria-label={`Ir al paso ${idx + 1}: ${item.label}`}
                  aria-current={isCurrent ? "step" : undefined}
                >
                  <span
                    className={`block h-2.5 rounded-full transition-all ${
                      isDone
                        ? "bg-green-500"
                        : isCurrent
                          ? "bg-primary-500 ring-2 ring-primary-200"
                          : "bg-gray-200 group-hover:bg-gray-300"
                    }`}
                  />
                </button>
              );
            })}
          </div>
          <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight">
            {stepShortLabel}
          </h2>
          {!completed && (
            <p className="mt-1 text-[11px] text-gray-500">
              Puedes cerrar y continuar más tarde — tu progreso se guarda
              automáticamente.
            </p>
          )}
        </div>

        {error && (
          <div
            ref={errorRef}
            className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm flex items-start gap-2"
          >
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Step content */}
        {step === 1 && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Tome una foto clara del <strong>frente</strong> del documento o
              escanéelo con la cámara.
            </p>

            {/* Preview slot */}
            <div className="relative aspect-[3/2] bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 mb-3">
              {previewUrlFront ? (
                <img
                  src={previewUrlFront}
                  alt="Anverso del documento"
                  className="w-full h-full object-contain"
                />
              ) : consent.idDocumentFront || consent.idDocument ? (
                <EncryptedDocumentView
                  url={(consent.idDocumentFront || consent.idDocument)?.url}
                  encryption={
                    (consent.idDocumentFront || consent.idDocument)
                      ?.encryption as EncryptionMeta | undefined
                  }
                  contentType={
                    (consent.idDocumentFront || consent.idDocument)?.contentType
                  }
                  alt="Anverso del documento"
                  className="w-full h-full object-contain"
                  isPdf={
                    (consent.idDocumentFront || consent.idDocument)
                      ?.contentType === "application/pdf"
                  }
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2">
                  <FileText className="w-10 h-10" />
                  <span className="text-xs">Sin documento</span>
                </div>
              )}
              {(consent.idDocumentFront ||
                consent.idDocument ||
                previewUrlFront) && (
                <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Subido
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="primary"
                disabled={loading}
                onClick={() => setScanningStep(1)}
                className="flex-1 justify-center py-3"
              >
                <Camera className="w-5 h-5 mr-2" />
                Tomar foto
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                onClick={() => fileInputRefFront.current?.click()}
                className="flex-1 justify-center py-3"
              >
                <Upload className="w-5 h-5 mr-2" />
                {consent.idDocumentFront ||
                consent.idDocument ||
                previewUrlFront
                  ? "Reemplazar"
                  : "Subir archivo"}
              </Button>
            </div>

            <input
              ref={fileInputRefFront}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleIdFrontUpload(f);
                e.target.value = "";
              }}
            />

            {scanningStep === 1 && (
              <DocumentScanner
                label="Documento de identidad (Anverso)"
                onCapture={(file) => {
                  setScanningStep(null);
                  handleIdFrontUpload(file);
                }}
                onClose={() => setScanningStep(null)}
              />
            )}

            {(consent.idDocumentFront || consent.idDocument) && !loading && (
              <Button
                type="button"
                variant="primary"
                className="mt-5 w-full py-3.5 text-base font-semibold"
                onClick={() => setStep(siguienteVisible(1))}
              >
                Continuar →
              </Button>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Tome una foto clara del <strong>reverso</strong> (parte de atrás)
              del documento.
            </p>

            <div className="relative aspect-[3/2] bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 mb-3">
              {previewUrlBack ? (
                <img
                  src={previewUrlBack}
                  alt="Reverso del documento"
                  className="w-full h-full object-contain"
                />
              ) : consent.idDocumentBack ? (
                <EncryptedDocumentView
                  url={consent.idDocumentBack.url}
                  encryption={
                    consent.idDocumentBack.encryption as
                      | EncryptionMeta
                      | undefined
                  }
                  contentType={consent.idDocumentBack.contentType}
                  alt="Reverso del documento"
                  className="w-full h-full object-contain"
                  isPdf={
                    consent.idDocumentBack.contentType === "application/pdf"
                  }
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2">
                  <FileText className="w-10 h-10" />
                  <span className="text-xs">Sin documento</span>
                </div>
              )}
              {(consent.idDocumentBack || previewUrlBack) && (
                <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Subido
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="primary"
                disabled={loading}
                onClick={() => setScanningStep(2)}
                className="flex-1 justify-center py-3"
              >
                <Camera className="w-5 h-5 mr-2" />
                Tomar foto
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                onClick={() => fileInputRefBack.current?.click()}
                className="flex-1 justify-center py-3"
              >
                <Upload className="w-5 h-5 mr-2" />
                {consent.idDocumentBack || previewUrlBack
                  ? "Reemplazar"
                  : "Subir archivo"}
              </Button>
            </div>

            <input
              ref={fileInputRefBack}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleIdBackUpload(f);
                e.target.value = "";
              }}
            />

            {scanningStep === 2 && (
              <DocumentScanner
                label="Documento de identidad (Reverso)"
                onCapture={(file) => {
                  setScanningStep(null);
                  handleIdBackUpload(file);
                }}
                onClose={() => setScanningStep(null)}
              />
            )}

            {consent.idDocumentBack && !loading && (
              <Button
                type="button"
                variant="primary"
                className="mt-5 w-full py-3.5 text-base font-semibold"
                onClick={() => setStep(siguienteVisible(2))}
              >
                Continuar →
              </Button>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Suba el certificado vigente de afiliación a{" "}
              <strong>EPS o Sisbén</strong>.
            </p>

            <div className="relative aspect-[3/2] bg-gray-100 rounded-xl overflow-hidden border-2 border-dashed border-gray-300 mb-3">
              {previewUrlEps ? (
                <img
                  src={previewUrlEps}
                  alt="Certificado EPS"
                  className="w-full h-full object-contain"
                />
              ) : consent.epsCertificate ? (
                <EncryptedDocumentView
                  url={consent.epsCertificate.url}
                  encryption={
                    consent.epsCertificate.encryption as
                      | EncryptionMeta
                      | undefined
                  }
                  contentType={consent.epsCertificate.contentType}
                  alt="Certificado EPS"
                  className="w-full h-full object-contain"
                  isPdf={
                    consent.epsCertificate.contentType === "application/pdf"
                  }
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400 gap-2">
                  <FileText className="w-10 h-10" />
                  <span className="text-xs">Sin certificado</span>
                </div>
              )}
              {(consent.epsCertificate || previewUrlEps) && (
                <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Subido
                </span>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="primary"
                disabled={loading}
                onClick={() => setScanningStep(3)}
                className="flex-1 justify-center py-3"
              >
                <Camera className="w-5 h-5 mr-2" />
                Tomar foto
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                onClick={() => fileInputRefEps.current?.click()}
                className="flex-1 justify-center py-3"
              >
                <Upload className="w-5 h-5 mr-2" />
                {consent.epsCertificate || previewUrlEps
                  ? "Reemplazar"
                  : "Subir archivo"}
              </Button>
            </div>

            <input
              ref={fileInputRefEps}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleEpsUpload(f);
                e.target.value = "";
              }}
            />

            {scanningStep === 3 && (
              <DocumentScanner
                label="Certificado de EPS"
                onCapture={(file) => {
                  setScanningStep(null);
                  handleEpsUpload(file);
                }}
                onClose={() => setScanningStep(null)}
              />
            )}

            {consent.epsCertificate && !loading && (
              <Button
                type="button"
                variant="primary"
                className="mt-5 w-full py-3.5 text-base font-semibold"
                onClick={() => setStep(siguienteVisible(3))}
              >
                Continuar →
              </Button>
            )}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-5">
            <p className="text-sm text-gray-600">
              Confirme su información médica y los lugares de salida y regreso.
              Estos datos se usarán en el documento oficial de autorización.
            </p>

            <EpsSelect
              label="EPS o entidad de salud *"
              value={medicalDraft.healthInsurance}
              onChange={(v) =>
                setMedicalDraft((d) => ({
                  ...d,
                  healthInsurance: v,
                }))
              }
              allowNone
              helperText="Si no aparece, selecciona 'Otra' y escribe el nombre"
            />

            <div className="rounded-lg border border-gray-200 p-3 space-y-3 bg-gray-50">
              <label className="flex items-start gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-1 w-4 h-4 accent-primary-600"
                  checked={medicalDraft.noMedicalConditions}
                  onChange={(e) =>
                    setMedicalDraft((d) => ({
                      ...d,
                      noMedicalConditions: e.target.checked,
                    }))
                  }
                />
                <span className="text-sm text-gray-800">
                  <strong>No presento</strong> condiciones médicas, alergias ni
                  tomo medicamentos relevantes
                </span>
              </label>

              {!medicalDraft.noMedicalConditions && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Condiciones médicas / enfermedades
                    </label>
                    <textarea
                      rows={2}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      value={medicalDraft.medicalConditions}
                      onChange={(e) =>
                        setMedicalDraft((d) => ({
                          ...d,
                          medicalConditions: e.target.value,
                        }))
                      }
                      placeholder="Ej: asma, diabetes, hipertensión..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Alergias
                    </label>
                    <textarea
                      rows={2}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      value={medicalDraft.allergies}
                      onChange={(e) =>
                        setMedicalDraft((d) => ({
                          ...d,
                          allergies: e.target.value,
                        }))
                      }
                      placeholder="Ej: penicilina, mariscos, nueces..."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Medicamentos permanentes
                    </label>
                    <textarea
                      rows={2}
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                      value={medicalDraft.medications}
                      onChange={(e) =>
                        setMedicalDraft((d) => ({
                          ...d,
                          medications: e.target.value,
                        }))
                      }
                      placeholder="Indique nombre, dosis y frecuencia"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Ciudad de salida"
                value={medicalDraft.departureCity}
                onChange={(e) =>
                  setMedicalDraft((d) => ({
                    ...d,
                    departureCity: e.target.value,
                  }))
                }
                placeholder="Ej: Bogotá"
                required
              />
              <Input
                label="Ciudad de regreso"
                value={medicalDraft.returnCity}
                onChange={(e) =>
                  setMedicalDraft((d) => ({
                    ...d,
                    returnCity: e.target.value,
                  }))
                }
                placeholder="Ej: Bogotá"
                required
              />
              {!isMinor && (
                <Input
                  label="Lugar de expedición del documento"
                  value={medicalDraft.idExpeditionCity}
                  onChange={(e) =>
                    setMedicalDraft((d) => ({
                      ...d,
                      idExpeditionCity: e.target.value,
                    }))
                  }
                  placeholder="Ciudad donde se expidió su CC"
                  required
                  className="sm:col-span-2"
                />
              )}
            </div>

            <Button
              type="button"
              variant="primary"
              className="w-full py-3.5 text-base font-semibold"
              onClick={handleSubmitMedicalAndTravel}
              loading={loading}
              disabled={loading}
            >
              Guardar y continuar →
            </Button>
          </div>
        )}

        {step === 5 && (
          <div>
            <p className="text-sm text-gray-600 mb-3">
              Lea y firme para aceptar el tratamiento de sus datos personales.
            </p>
            <div className="text-xs text-gray-700 space-y-2 max-h-48 overflow-y-auto p-3 bg-gray-50 rounded-lg border">
              <p>
                Autorizo de manera previa, expresa, voluntaria e informada a la
                Iglesia Adventista del Séptimo Día, a la Unión Colombiana del
                Norte, a {associationName} y al club organizador, para
                recolectar, almacenar, usar, circular y procesar mis datos
                personales (incluyendo datos sensibles como información médica)
                con la finalidad de: gestionar mi inscripción y participación en
                el evento, contactar en caso de emergencia, generar estadísticas
                internas, y emitir comunicaciones institucionales.
              </p>
              <p>
                El tratamiento se realiza conforme a la Ley 1581 de 2012, su
                Decreto 1377 de 2013 y normas concordantes. Conozco que asiste
                el derecho a conocer, actualizar, rectificar y solicitar la
                supresión de mis datos en los términos legales.
              </p>
            </div>
            {consent.dataConsent?.accepted && consent.dataConsent?.signature ? (
              <div className="mt-3 flex items-center gap-2 text-green-700 text-sm">
                <CheckCircle2 className="w-4 h-4" /> Aceptado y firmado
              </div>
            ) : (
              <>
                <div className="mt-4">
                  <SignaturePad
                    label="Firma de aceptación"
                    hint="Firme dentro del recuadro para aceptar el tratamiento de datos"
                    onConfirm={(d) => setDataConsentSignature(d)}
                    onClear={() => setDataConsentSignature(null)}
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  className="mt-5 w-full py-3.5 text-base font-semibold"
                  onClick={handleAcceptData}
                  loading={loading}
                  disabled={loading || !dataConsentSignature}
                >
                  Acepto el tratamiento de datos
                </Button>
              </>
            )}
          </div>
        )}

        {step === 6 && !isMinor && step6Substep === "preview" && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900">
              <Eye className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="text-xs">
                <strong>Revise atentamente el documento</strong> que va a
                firmar. Verifique sus datos personales, EPS, condiciones médicas
                y ciudades. Si algo está mal, vuelva a editarlo antes de firmar.
              </p>
            </div>
            {previewPdfUrl ? (
              <PdfPreview
                url={previewPdfUrl}
                fileName={`consentimiento-${person.fullName.replace(/\s+/g, "-")}.pdf`}
                desktopHeight="60vh"
              />
            ) : (
              <div className="h-[60vh] flex items-center justify-center text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 py-3 text-base"
                onClick={() => {
                  clearPreviewPdf();
                  setStep(4);
                }}
                disabled={loading}
              >
                <Pencil className="w-4 h-4 mr-2" /> Editar mis datos
              </Button>
              <Button
                type="button"
                variant="primary"
                className="flex-1 py-3 text-base font-semibold"
                onClick={() => setStep6Substep("sign")}
                disabled={loading || !previewPdfUrl}
              >
                He revisado, firmar →
              </Button>
            </div>
          </div>
        )}

        {step === 6 && !isMinor && step6Substep === "sign" && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Firme dentro del recuadro. Al confirmar se guardará el documento
              firmado.
            </p>
            <SignaturePad
              label="Firma del participante"
              hint="Firme dentro del recuadro y presione Confirmar"
              onConfirm={(d) => setParticipantSig(d)}
              onClear={() => setParticipantSig(null)}
            />
            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <Button
                type="button"
                variant="outline"
                className="sm:w-1/3 py-3 text-base"
                onClick={() => setStep6Substep("preview")}
                disabled={loading}
              >
                ← Volver al documento
              </Button>
              <Button
                type="button"
                variant="primary"
                className="flex-1 py-3.5 text-base font-semibold"
                onClick={handleAdultSubmit}
                loading={loading}
                disabled={loading || !participantSignature}
              >
                Confirmar y guardar
              </Button>
            </div>
          </div>
        )}

        {step === 6 && isMinor && step6Substep === "form" && (
          <div className="space-y-6">
            <p className="text-sm text-gray-600">
              Este formato debe ser diligenciado y firmado por el padre, madre o
              acudiente legal del menor. Se requiere también una foto de quien
              firma para verificación.
            </p>

            {parents.map((p, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-3 sm:p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm">
                    Padre/Madre/Acudiente {idx + 1}
                  </h4>
                  {parents.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="xs"
                      onClick={() => removeParent(idx)}
                      disabled={loading}
                    >
                      Quitar
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input
                    label="Nombre completo"
                    value={p.fullName}
                    onChange={(e) =>
                      updateParent(idx, { fullName: e.target.value })
                    }
                    required
                  />
                  <Select
                    label="Relación"
                    value={p.relationship}
                    onChange={(e) =>
                      updateParent(idx, {
                        relationship: e.target
                          .value as ParentDraft["relationship"],
                      })
                    }
                  >
                    <option value="padre">Padre</option>
                    <option value="madre">Madre</option>
                    <option value="acudiente">Acudiente legal</option>
                  </Select>
                  <Select
                    label="Tipo de documento"
                    value={p.idDocumentType}
                    onChange={(e) =>
                      updateParent(idx, {
                        idDocumentType: e.target.value as IdDocumentType,
                      })
                    }
                  >
                    <option value="CC">Cédula de ciudadanía</option>
                    <option value="CE">Cédula de extranjería</option>
                    <option value="PASAPORTE">Pasaporte</option>
                    <option value="PPT">Permiso por Protección Temporal (PPT)</option>
                  </Select>
                  <Input
                    label="Número de documento"
                    value={p.idDocumentNumber}
                    onChange={(e) =>
                      updateParent(idx, {
                        idDocumentNumber: e.target.value,
                      })
                    }
                    required
                  />
                  <Input
                    label="Domicilio"
                    value={p.address}
                    onChange={(e) =>
                      updateParent(idx, { address: e.target.value })
                    }
                    required
                    className="sm:col-span-2"
                  />
                </div>

                <ParentPhotoCapture
                  defaultFacingMode="environment"
                  onCapture={(file) => updateParent(idx, { photoFile: file })}
                  onClear={() => updateParent(idx, { photoFile: undefined })}
                />
              </div>
            ))}

            {parents.length < 2 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addParent}
                disabled={loading}
              >
                + Agregar segundo padre/acudiente
              </Button>
            )}

            <Button
              type="button"
              variant="primary"
              onClick={handleMinorGoToPreview}
              loading={loading}
              disabled={loading}
              className="w-full py-3.5 text-base font-semibold"
            >
              Continuar → Ver el documento
            </Button>
          </div>
        )}

        {step === 6 && isMinor && step6Substep === "preview" && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900">
              <Eye className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p className="text-xs">
                <strong>Revisen atentamente el documento</strong> que van a
                firmar. Verifiquen los datos del menor, los datos del/los
                padre(s) o acudiente(s) y las condiciones médicas. Si algo está
                mal, pueden volver a editarlo antes de firmar.
              </p>
            </div>
            {previewPdfUrl ? (
              <PdfPreview
                url={previewPdfUrl}
                fileName={`consentimiento-${person.fullName.replace(/\s+/g, "-")}.pdf`}
                desktopHeight="60vh"
              />
            ) : (
              <div className="h-[60vh] flex items-center justify-center text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 py-3 text-base"
                  onClick={() => {
                    clearPreviewPdf();
                    setStep(4);
                  }}
                  disabled={loading}
                >
                  <Pencil className="w-4 h-4 mr-2" /> Editar datos médicos
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 py-3 text-base"
                  onClick={() => {
                    clearPreviewPdf();
                    setStep6Substep("form");
                  }}
                  disabled={loading}
                >
                  <Pencil className="w-4 h-4 mr-2" /> Editar datos del padre
                </Button>
              </div>
              <Button
                type="button"
                variant="primary"
                className="py-3 text-base font-semibold"
                onClick={() => setStep6Substep("sign")}
                disabled={loading || !previewPdfUrl}
              >
                He revisado, firmar →
              </Button>
            </div>
          </div>
        )}

        {step === 6 && isMinor && step6Substep === "sign" && (
          <div className="space-y-6">
            <p className="text-sm text-gray-600">
              Cada padre/acudiente debe firmar dentro del recuadro
              correspondiente.
            </p>
            {parents.map((p, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg p-4 space-y-2"
              >
                <h4 className="font-semibold text-sm">
                  Firma de {p.fullName || `padre/acudiente ${idx + 1}`}
                </h4>
                <SignaturePad
                  label="Firma"
                  initialDataUrl={p.signatureDataUrl}
                  onConfirm={(d) => updateParent(idx, { signatureDataUrl: d })}
                  onClear={() =>
                    updateParent(idx, { signatureDataUrl: undefined })
                  }
                />
              </div>
            ))}
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                className="sm:w-1/3 py-3 text-base"
                onClick={() => setStep6Substep("preview")}
                disabled={loading}
              >
                ← Volver al documento
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleMinorSubmit}
                loading={loading}
                disabled={loading}
                className="flex-1 py-3.5 text-base font-semibold"
              >
                Confirmar y guardar
              </Button>
            </div>
          </div>
        )}

        {step === 7 && !completed && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Tome una foto clara <strong>de frente</strong> del participante
              para verificación de identidad.
            </p>

            {participantPhotoPreview ? (
              <div className="relative aspect-square max-w-[220px] mx-auto mb-3 rounded-full overflow-hidden border-4 border-blue-200 shadow">
                <img
                  src={participantPhotoPreview}
                  alt="Foto del participante"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  className="absolute bottom-2 right-2 bg-white rounded-full p-1 shadow text-gray-600"
                  onClick={() => setParticipantPhoto(null)}
                  title="Volver a tomar la foto"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
            ) : consent.participantPhoto ? (
              <>
                <div className="relative aspect-square max-w-[220px] mx-auto mb-3 rounded-full overflow-hidden border-4 border-green-200 shadow">
                  <EncryptedDocumentView
                    url={consent.participantPhoto.url}
                    encryption={
                      consent.participantPhoto.encryption as
                        | EncryptionMeta
                        | undefined
                    }
                    contentType={consent.participantPhoto.contentType}
                    alt="Foto del participante"
                    className="w-full h-full object-cover"
                  />
                  <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Registrada
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-center">
                  <button
                    type="button"
                    className="text-xs text-primary-600 hover:underline flex items-center gap-1"
                    onClick={() => setParticipantPhoto(null)}
                  >
                    <RefreshCw className="w-3 h-3" /> Volver a tomar
                  </button>
                </div>
              </>
            ) : (
              <ParentPhotoCapture
                label="Foto del participante"
                hint="Use la cámara frontal. Asegúrese de que el rostro esté bien iluminado."
                onCapture={(file) => setParticipantPhoto(file)}
                onClear={() => setParticipantPhoto(null)}
              />
            )}

            <Button
              type="button"
              variant="primary"
              className="mt-5 w-full py-3.5 text-base font-semibold"
              onClick={handleParticipantPhotoSubmit}
              loading={loading}
              disabled={
                loading || (!participantPhotoFile && !consent.participantPhoto)
              }
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo...
                </>
              ) : (
                "Completar check-in"
              )}
            </Button>
          </div>
        )}

        {completed && (
          <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 flex items-start gap-2">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <div>
              <p className="font-semibold">¡Check-in completado!</p>
              <p className="text-sm">
                Su consentimiento ha sido firmado y registrado.
              </p>
              {consent.pdfDocument && (
                <CompletedPdfLink pdfDocument={consent.pdfDocument} />
              )}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

/**
 * Link "Ver PDF generado" que descarga el `.enc` cifrado de Storage,
 * lo desencripta en el cliente con la clave AES guardada en Firestore
 * y abre el blob resultante en una nueva pestaña como PDF real.
 *
 * Antes este link apuntaba directo a `pdfDocument.url`, que sirve el
 * ciphertext binario — el navegador descargaba un archivo ilegible.
 */
function CompletedPdfLink({ pdfDocument }: { pdfDocument: ConsentDocument }) {
  const { decryptedUrl, isDecrypting, error } = useDecryptedUrl({
    url: pdfDocument.url,
    encryption: pdfDocument.encryption as EncryptionMeta | undefined,
    contentType: pdfDocument.contentType ?? "application/pdf",
  });

  if (isDecrypting) {
    return (
      <span className="text-sm mt-1 inline-flex items-center gap-1 text-gray-600">
        <Loader2 className="w-3 h-3 animate-spin" /> Preparando PDF…
      </span>
    );
  }

  if (error) {
    return <span className="text-sm mt-1 text-red-600 block">{error}</span>;
  }

  if (!decryptedUrl) return null;

  return (
    <a
      href={decryptedUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm underline mt-1 inline-block"
    >
      Ver PDF generado
    </a>
  );
}

export default ConsentCheckinModal;
